// تفسیر GS1 DataMatrix / GS1-128 در Frontend (AC-INV-003.1؛ هم‌ارز Gs1Parser سرور در Phoenix.Inventory.Domain): شکل خام با FNC1 (GS، U+001D)
// و شناسه نماد اختیاری (]d2، ]C1، ]Q3) یا شکل پرانتزی (01)…(17)…(10)…؛ AI 01 GTIN، 10 بچ، 11 تولید، 17 انقضا (روز 00 ← آخرین روز ماه)، 21 سریال.
// سال دو رقمی 20YY (C-AGENT-763). بدون وابستگی؛ فقط برای پر کردن فوری فیلدها — تفسیر قطعی و یافتن کالا با POST scans:resolve.

export const GROUP_SEPARATOR = '\u001d';

export interface Gs1Parsed {
  gtin: string | null;
  batchNumber: string | null;
  /** تاریخ کسب‌وکاری yyyy-MM-dd. */
  expiryDate: string | null;
  productionDate: string | null;
  serialNumber: string | null;
  elements: Record<string, string>;
}

export type Gs1Result = { ok: true; data: Gs1Parsed } | { ok: false; reason: string };

const SYMBOLOGY = [']d2', ']C1', ']Q3', ']e0', ']J1'];
const FIXED: Record<string, number> = { '00': 18, '01': 14, '02': 14, '11': 6, '12': 6, '13': 6, '15': 6, '16': 6, '17': 6, '20': 2 };
const VARIABLE: Record<string, number> = {
  '10': 20,
  '21': 20,
  '22': 20,
  '30': 8,
  '37': 8,
  '90': 30,
  '235': 28,
  '240': 30,
  '241': 30,
  '242': 6,
  '250': 30,
  '400': 30,
  '710': 20,
  '711': 20,
  '712': 20,
  '713': 20,
  '714': 20,
  '715': 20,
};

const isDigits = (value: string) => /^[0-9]+$/.test(value);

/** ارقام فارسی و عربی (اسکنر در حالت صفحه‌کلید فارسی) و نشانه متنی <GS>. */
function clean(raw: string): string {
  return raw
    .trim()
    .replace(/<GS>/gi, GROUP_SEPARATOR)
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660));
}

/** GTIN معتبر (۸، ۱۲، ۱۳ یا ۱۴ رقم با رقم کنترل). */
export function isValidGtin(value: string | null | undefined): boolean {
  if (!value || ![8, 12, 13, 14].includes(value.length) || !isDigits(value)) {
    return false;
  }
  let sum = 0;
  for (let index = value.length - 2, weight = 3; index >= 0; index--, weight = 4 - weight) {
    sum += Number(value[index]) * weight;
  }
  return (10 - (sum % 10)) % 10 === Number(value[value.length - 1]);
}

/** نرمال ۱۴ رقمی؛ نامعتبر ← null. */
export function normalizeGtin(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return isValidGtin(trimmed) ? trimmed.padStart(14, '0') : null;
}

/** تاریخ YYMMDD ← yyyy-MM-dd (روز ۰۰ ← آخرین روز ماه). */
export function parseGs1Date(value: string | undefined): string | null {
  if (!value || value.length !== 6 || !isDigits(value)) {
    return null;
  }
  const year = 2000 + Number(value.slice(0, 2));
  const month = Number(value.slice(2, 4));
  let day = Number(value.slice(4, 6));
  if (month < 1 || month > 12) {
    return null;
  }
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day === 0) {
    day = last;
  }
  if (day > last) {
    return null;
  }
  return `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function readAi(text: string, position: number): { ai: string; length: number; variable: boolean } | null {
  const two = text.slice(position, position + 2);
  if (!/^[0-9]{2}$/.test(two)) {
    return null;
  }
  const fixed = FIXED[two];
  if (fixed !== undefined) {
    return { ai: two, length: fixed, variable: false };
  }
  const variable = VARIABLE[two];
  if (variable !== undefined) {
    return { ai: two, length: variable, variable: true };
  }
  if (two.startsWith('9')) {
    return { ai: two, length: 90, variable: true };
  }
  const three = text.slice(position, position + 3);
  if (/^[0-9]{3}$/.test(three)) {
    const variableThree = VARIABLE[three];
    if (variableThree !== undefined) {
      return { ai: three, length: variableThree, variable: true };
    }
    if (/^41[0-7]$/.test(three)) {
      return { ai: three, length: 13, variable: false };
    }
  }
  const four = text.slice(position, position + 4);
  if (/^3[1-6][0-9]{2}$/.test(four)) {
    return { ai: four, length: 6, variable: false };
  }
  return null;
}

const printable = (value: string) => /^[!-~]+$/.test(value);

/** شکل GS1 دارد (شناسه نماد، FNC1، پرانتز یا AI 01 با GTIN معتبر)؛ بارکد ساده ← false. */
export function looksLikeGs1(raw: string | null | undefined): boolean {
  if (!raw?.trim()) {
    return false;
  }
  const text = clean(raw);
  if (SYMBOLOGY.some((prefix) => text.startsWith(prefix)) || text.includes(GROUP_SEPARATOR) || text.startsWith('(')) {
    return true;
  }
  return text.length >= 16 && text.startsWith('01') && isValidGtin(text.slice(2, 16));
}

/** تفسیر رشته عناصر GS1. */
export function parseGs1(raw: string | null | undefined): Gs1Result {
  if (!raw?.trim()) {
    return { ok: false, reason: 'empty' };
  }
  let text = clean(raw);
  const prefix = SYMBOLOGY.find((candidate) => text.startsWith(candidate));
  if (prefix) {
    text = text.slice(prefix.length);
  }
  while (text.startsWith(GROUP_SEPARATOR)) {
    text = text.slice(1);
  }
  if (!text) {
    return { ok: false, reason: 'empty' };
  }
  const elements: Record<string, string> = {};
  if (text.startsWith('(')) {
    const pattern = /\(([0-9]{2,4})\)([^(]*)/g;
    let consumed = 0;
    for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
      if (match.index !== consumed) {
        return { ok: false, reason: 'characters' };
      }
      consumed = match.index + match[0].length;
      const ai = match[1] ?? '';
      const value = (match[2] ?? '').trim().replace(new RegExp(`${GROUP_SEPARATOR}+$`), '');
      const known = readAi(ai, 0);
      if (!known || known.ai.length !== ai.length) {
        return { ok: false, reason: 'unknown_ai' };
      }
      const badLength = known.variable ? value.length === 0 || value.length > known.length : value.length !== known.length || !isDigits(value);
      if (badLength) {
        return { ok: false, reason: 'length' };
      }
      if (!printable(value)) {
        return { ok: false, reason: 'characters' };
      }
      elements[ai] = value;
    }
    if (consumed !== text.length) {
      return { ok: false, reason: 'characters' };
    }
  } else {
    let position = 0;
    while (position < text.length) {
      if (text[position] === GROUP_SEPARATOR) {
        position += 1;
        continue;
      }
      const known = readAi(text, position);
      if (!known) {
        return { ok: false, reason: 'unknown_ai' };
      }
      position += known.ai.length;
      let value: string;
      if (known.variable) {
        const end = text.indexOf(GROUP_SEPARATOR, position);
        const stop = end < 0 ? text.length : end;
        value = text.slice(position, stop);
        position = stop;
        if (value.length === 0 || value.length > known.length) {
          return { ok: false, reason: 'length' };
        }
      } else {
        if (position + known.length > text.length) {
          return { ok: false, reason: 'length' };
        }
        value = text.slice(position, position + known.length);
        position += known.length;
        if (!isDigits(value)) {
          return { ok: false, reason: 'characters' };
        }
      }
      if (!printable(value)) {
        return { ok: false, reason: 'characters' };
      }
      elements[known.ai] = value;
    }
  }
  if (Object.keys(elements).length === 0) {
    return { ok: false, reason: 'not_gs1' };
  }
  const gtin = elements['01'] ?? elements['02'] ?? null;
  if (gtin !== null && !isValidGtin(gtin)) {
    return { ok: false, reason: 'gtin_check_digit' };
  }
  const expiryDate = elements['17'] === undefined ? null : parseGs1Date(elements['17']);
  const productionDate = elements['11'] === undefined ? null : parseGs1Date(elements['11']);
  if ((elements['17'] !== undefined && !expiryDate) || (elements['11'] !== undefined && !productionDate)) {
    return { ok: false, reason: 'date' };
  }
  return {
    ok: true,
    data: {
      gtin,
      batchNumber: elements['10'] ?? null,
      expiryDate,
      productionDate,
      serialNumber: elements['21'] ?? null,
      elements,
    },
  };
}
